// User types
export enum UserRole {
  QUESTION_OWNER = "question_owner",
  RESPONDER = "responder",
  ADMIN = "admin",
}

export enum UserStatus {
  ACTIVE = "active",
  SUSPENDED = "suspended",
  PENDING = "pending",
}

export enum RatingTier {
  GOLD = "gold",
  SILVER = "silver",
  BRONZE = "bronze",
  PROBATION = "probation",
}

// Survey types
export enum SurveyStatus {
  FUNDED = "funded",
  ACTIVE = "active",
  COLLECTING = "collecting",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  ANALYZED = "analyzed",
}

export enum DistributionMode {
  ASAP = "asap",
  EFFICIENT = "efficient",
  ECO = "eco",
}

export enum QuestionType {
  MULTIPLE_CHOICE = "multiple_choice",
}

export enum ParticipationStatus {
  INVITED = "invited",
  ACCEPTED = "accepted",
  DECLINED = "declined",
  COMPLETED = "completed",
  EXPIRED = "expired",
}

export enum TransactionType {
  DEPOSIT = "deposit",
  WITHDRAWAL = "withdrawal",
  REWARD_EARNED = "reward_earned",
  SURVEY_PAYMENT = "survey_payment",
  PLATFORM_FEE = "platform_fee",
  REFUND = "refund",
}

// Database entity types
export interface User {
  id: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  email_verified: boolean;
  oauth_provider: string | null;
  oauth_provider_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  demographics: Record<string, unknown> | null;
  completion_percentage: number;
  created_at: string;
  updated_at: string;
}

export interface ResponderRating {
  id: string;
  user_id: string;
  accuracy_score: number;
  completion_rate: number;
  response_time_avg: number;
  total_responses: number;
  correct_responses: number;
  overall_rating: number;
  tier: RatingTier;
  verification_surveys_completed: number;
  last_tier_change: string | null;
  updated_at: string;
}

export interface Wallet {
  id: string;
  user_id: string;
  balance: string;
  locked_balance: string;
  external_address: string | null;
  created_at: string;
  updated_at: string;
}

export interface Survey {
  id: string;
  owner_id: string;
  title: string;
  description: string | null;
  status: SurveyStatus;
  distribution_mode: DistributionMode;
  budget: string;
  target_responses: number;
  actual_responses: number;
  reward_per_response: string | null;
  start_date: string | null;
  end_date: string | null;
  response_window_hours: number;
  created_at: string;
  updated_at: string;
}

export interface Question {
  id: string;
  survey_id: string;
  question_text: string;
  question_type: QuestionType;
  display_order: number;
  created_at: string;
}

export interface Option {
  id: string;
  question_id: string;
  option_text: string;
  display_order: number;
  response_count: number;
  created_at: string;
}

export interface Response {
  id: string;
  survey_id: string;
  question_id: string;
  responder_id: string;
  selected_option_id: string;
  is_majority: boolean | null;
  reward_amount: string | null;
  response_time_seconds: number | null;
  submitted_at: string;
}

export interface SurveyParticipation {
  id: string;
  survey_id: string;
  responder_id: string;
  status: ParticipationStatus;
  invited_at: string;
  responded_at: string | null;
  expires_at: string | null;
}

export interface Transaction {
  id: string;
  wallet_id: string;
  type: TransactionType;
  amount: string;
  balance_after: string;
  description: string | null;
  reference_id: string | null;
  created_at: string;
}

// API types
export interface JWTPayload {
  user_id: string;
  email: string;
  role: string;
  exp?: number;
  iat?: number;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
}

export interface UserResponse {
  user: User;
  tokens: AuthTokens;
}

export interface CreateUserInput {
  email: string;
  password: string;
  role: UserRole;
  display_name?: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface CreateSurveyInput {
  title: string;
  description?: string;
  budget: string;
  target_responses: number;
  distribution_mode: DistributionMode;
  start_date?: string;
  end_date?: string;
  response_window_hours?: number;
  question: CreateQuestionInput;
}

export interface CreateQuestionInput {
  question_text: string;
  options: string[];
}

export interface SubmitResponseInput {
  survey_id: string;
  question_id: string;
  selected_option_id: string;
  response_time_seconds?: number;
}

// Environment types
export interface Env {
  // Cloudflare bindings
  DB: D1Database;
  KV: KVNamespace;
  R2: R2Bucket;

  // JWT
  JWT_SECRET: string;
  ACCESS_TOKEN_EXPIRE_MINUTES: string;
  REFRESH_TOKEN_EXPIRE_DAYS: string;

  // OAuth
  GOOGLE_CLIENT_ID?: string;
  GOOGLE_CLIENT_SECRET?: string;
  GITHUB_CLIENT_ID?: string;
  GITHUB_CLIENT_SECRET?: string;
  MOLTBOOK_CLIENT_ID?: string;
  MOLTBOOK_CLIENT_SECRET?: string;

  // Blockchain
  WEB3_PROVIDER_URL?: string;
  POLYGON_PROVIDER_URL?: string;
  CONTRACT_ADDRESS?: string;
  PLATFORM_WALLET_ADDRESS?: string;
  PLATFORM_WALLET_PRIVATE_KEY?: string;

  // Platform settings
  PLATFORM_FEE_PERCENTAGE?: string;
  MIN_COIN_RATE?: string;
  MIN_OPTIONS_RESPONDER_MULTIPLIER?: string;
  MOCK_MOLTBOOK?: string;

  // CORS
  CORS_ORIGINS?: string;
}

// Cloudflare Workers types
declare global {
  interface D1Result {
    meta: {
      duration: number;
      last_row_id: number | null;
      changes: number;
      served_by: string;
    };
    rows: unknown[];
    success: boolean;
    error: unknown | null;
  }

  interface D1Database {
    prepare(query: string): D1PreparedStatement;
    batch(statements: D1PreparedStatement[]): Promise<D1Result[]>;
    exec(query: string): Promise<D1Result>;
    dump(): Promise<unknown>;
  }

  interface D1PreparedStatement {
    bind(...values: unknown[]): D1PreparedStatement;
    first<T = unknown>(): Promise<T | null>;
    all<T = unknown>(): Promise<{ results: T[]; success: boolean; error: unknown | null; meta: unknown }>;
    run(): Promise<D1Result>;
  }

  interface KVNamespace {
    get(key: string): Promise<string | null>;
    get(key: string, type: "text"): Promise<string | null>;
    get(key: string, type: "json"): Promise<unknown | null>;
    get(key: string, type: "arrayBuffer"): Promise<ArrayBuffer | null>;
    get(key: string, type: "stream"): Promise<ReadableStream | null>;
    put(key: string, value: string | ReadableStream | ArrayBuffer, options?: {
      expiration?: number;
      expirationTtl?: number;
      metadata?: unknown;
    }): Promise<void>;
    delete(key: string): Promise<void>;
    list(options?: { cursor?: string; limit?: number; prefix?: string }): Promise<{
      keys: Array<{ name: string; metadata?: unknown; expiration?: number }>;
      list_complete: boolean;
      cursor: string;
    }>;
  }

  interface R2Bucket {
    put(key: string, value: unknown | ReadableStream | ArrayBuffer | ArrayBufferView, options?: {
      httpMetadata?: { contentType?: string };
      customMetadata?: Record<string, string>;
    }): Promise<R2Object>;
    get(key: string): Promise<R2Object | null>;
    delete(keys: string | string[]): Promise<{ deleted: string[]; errors: Array<{ key: string; error: Error }> }>;
    list(options?: { prefix?: string; cursor?: string; limit?: number }): Promise<{
      objects: Array<{ key: string; size: number; uploaded: string }>;
      truncated: boolean;
      cursor?: string;
    }>;
  }

  interface R2Object {
    key: string;
    size: number;
    httpMetadata: { contentType: string };
    customMetadata: Record<string, string>;
    writeHttpMetadata(headers: Headers): void;
    arrayBuffer(): Promise<ArrayBuffer>;
    text(): Promise<string>;
    json(): Promise<unknown>;
    body: ReadableStream;
  }
}
